import { getSseApiUrl } from '@/constants';
import { API } from '@/redux/config/index';
import SimpleLoader from '@/shared/components/SimpleLoader';
import { LoadingWrap } from '@/shared/components/globalStyledComponents';
import { NotificationsIcon } from '@/shared/dynamicImports';
import {
  colorAccent,
  colorBackground,
  colorGray,
  colorNotificationBackgroundhover,
  colorText,
  textcolorNotificationBackgroundhover,
} from '@/utils/palette';
import dayjs from 'dayjs';
import { EventSourcePolyfill } from 'event-source-polyfill';
import ReactHtmlParser from 'html-react-parser';
import { lighten } from 'polished';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { healthCheck } from '@/redux/actions/authActions';
import EllipsisAnimation from '@/shared/components/EllipsisAnimation';
import useUrlQueryParams from '@/utils/hooks/useUrlQueryParams';

const NotificationIcon = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { encryptQueryParams } = useUrlQueryParams();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);

  const [clickedNotificationId, setClickedNotificationId] = useState(null);

  const [error, setError] = useState(null);
  const [retryAttempts, setRetryAttempts] = useState(0);
  const [sseError, setSseError] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [readNotificationIndices, setReadNotificationIndices] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const eventSourceRef = useRef(null);

  const getToken = JSON.parse(localStorage.getItem('token'));
  const token = getToken?.currentUser?.token?.token;

  const isNotificationRead = (notificationID) => {
    return (
      readNotificationIndices.includes(notificationID) || localStorage.getItem(`read_${notificationID}`) === 'true'
    );
  };

  const handleSSEError = (error) => {
    console.error('SSE Error:', error);
    setSseError(true);

    if (error.status === 412) {
      dispatch(healthCheck());
    }

    if (retryAttempts < 10) {
      setRetryAttempts((prevAttempts) => prevAttempts + 1);
      console.log(`Retrying SSE connection attempt ${retryAttempts + 1}...`);

      setTimeout(() => {
        reconnectSSE();
      }, 15000);
    } else {
      console.log('Max retry attempts reached. No more retries.');
      closeSSEConnection();
    }
  };

  const closeSSEConnection = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      console.log('SSE connection closed after 10 retries.');
    }
  };

  const reconnectSSE = () => {
    setSseError(false);
    eventSourceRef.current?.close();
    initializeSSEConnection();
  };

  const initializeSSEConnection = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.removeEventListener('message', handleEvent);
      eventSourceRef.current.close();
    }

    // setTimeout(() => {
    eventSourceRef.current = new EventSourcePolyfill(`${getSseApiUrl()}/subscribePushNotifications`, {
      headers: {
        token: JSON.parse(localStorage.getItem('token'))?.currentUser?.token?.token || token,
        'x-customer-id': localStorage.getItem('currentCustomerID'),
      },
      heartbeatTimeout: 300000,
    });

    eventSourceRef.current.addEventListener('message', handleEvent);

    if (retryAttempts <= 10) {
      eventSourceRef.current.onerror = (error) => {
        console.log('error_at_1004', error);
        handleSSEError(error);
      };
    }
    // }, 1000);
  };

  const handleEvent = async (event) => {
    // await new Promise((resolve) => setTimeout(resolve, 1000));

    const data = event.data
      .replace(/'/g, '"')
      .replace(/None/g, 'null')
      .replace(/True/g, 'true')
      .replace(/False/g, 'false');
    // const trimmedData = data.trim();
    // const newNotification = JSON.parse(trimmedData);
    try {
      const trimmedData = data.trim().replace(/^\uFEFF/, '');
      const newNotification = JSON.parse(trimmedData);

      if (!newNotification || typeof newNotification !== 'object') {
        console.error('Invalid notification format:', trimmedData);
        return;
      }

      console.log('New notification received_data:', newNotification);
      if (newNotification?.count) {
        setNotificationCount(newNotification?.count);
      }

      if (newNotification.read === true) {
        const isAlreadyRead = isNotificationRead(newNotification.notificationID);
        setReadNotificationIndices((prevIndices) =>
          isAlreadyRead ? prevIndices : [newNotification.notificationID, ...prevIndices],
        );
        if (!isAlreadyRead) {
          localStorage.setItem(`read_${newNotification.notificationID}`, newNotification.read);
        }
      }
      if (newNotification?.notificationID) {
        setNotifications((prevNotifications) => [newNotification, ...prevNotifications]);
      }

      toast.info(newNotification.message, {
        position: 'bottom-right',
        autoClose: 5000,
        hideProgressBar: false,
      });
    } catch (error) {
      console.error('Error parsing notification:', error);
      setError(error.message);
    }
  };

  const [notificationLoading, setNotificationLoading] = useState(false);
  const fetchData = async (retryCount = 0) => {
    try {
      setNotificationLoading(true);
      const userId = localStorage.getItem('userID');
      const response = await API.get(`/notifications/user/${userId}`);
      const data = response.data;

      data.forEach((newNotification) => {
        if (newNotification.read === true) {
          const isAlreadyRead = isNotificationRead(newNotification.notificationID);
          setReadNotificationIndices((prevIndices) =>
            isAlreadyRead ? prevIndices : [newNotification.notificationID, ...prevIndices],
          );
          if (!isAlreadyRead) {
            localStorage.setItem(`read_${newNotification.notificationID}`, newNotification.read);
          }
        }
      });

      setNotifications(data);
    } catch (error) {
      setError(error.message);
      console.error(error.message);
    } finally {
      setNotificationLoading(false);
    }
  };

  useEffect(() => {
    initializeSSEConnection();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.removeEventListener('message', handleEvent);
        eventSourceRef.current.close();
      }
    };
  }, []);

  const handleNotificationClick = () => {
    setNotificationCount(0);
    setIsNotificationOpen(!isNotificationOpen);
    setClickedNotificationId(null);
    if (!isNotificationOpen) {
      fetchData();
    }
  };

  // const handleCloseNotification = () => {
  //   setIsNotificationOpen(false);
  //   setClickedNotificationId(null);
  // };

  // const markAllNotificationsAsRead = () => {
  //   const allNotificationIds = notifications.map((notification) => notification.notificationID);
  //   setReadNotificationIndices(allNotificationIds);
  // };

  const handleNotificationItemClick = (notificationID, attributes) => {
    const indexOfClickedNotification = readNotificationIndices.indexOf(notificationID);
    if (indexOfClickedNotification === -1) {
      setReadNotificationIndices([...readNotificationIndices, notificationID]);
    } else {
      setReadNotificationIndices(readNotificationIndices.filter((item) => item !== notificationID));
    }
    setClickedNotificationId(notificationID);
    console.log('attributes==>', attributes);
    if (attributes?.module === 'documents') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        if (attributes?.subModule === 'diagrams') {
          const encryptedParams = encryptQueryParams({
            UniqueId: attributes?.signature?.UniqueId,
            ServerRelativeURL: attributes?.signature?.ServerRelativeURL,
          });
          navigate(`/documents/diagrams/${attributes?.Name}?${encryptedParams}`);
        } else if (attributes?.subModule === 'files') navigate(`/documents/files/`);
      }
    } else if (attributes?.module === 'projects') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        const encryptedParams = encryptQueryParams({ key: attributes?.signature?.projectKeyID });
        navigate(`/projects/${attributes?.signature?.projectKeyID}?${encryptedParams}`);
      }
    } else if (attributes?.module === 'tickets') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        // const encryptedParams = encryptQueryParams({ key: attributes?.signature?.key });
        navigate(`/tickets/${attributes?.signature?.key}`);
      }
    } else if (attributes?.module === 'reports') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        navigate(`/reports/`);
      }
    } else if (attributes?.module === 'vulnerability_scans') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        const encryptedParams = encryptQueryParams({ assessmentID: attributes?.signature?.assessmentID });
        navigate(`/vulnerability_scans/detail?${encryptedParams}`);
      }
    } else if (attributes?.module === 'assessments') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        const encryptedParams = encryptQueryParams({ assessmentID: attributes?.signature?.assessmentID });
        navigate(`/assessments/${attributes?.name}?${encryptedParams}`);
      }
    } else if (attributes?.module === 'virtual_machines_dashboard') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        navigate(`/virtual_machines_dashboard`);
      }
    } else if (attributes?.module === 'private_networks') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        navigate(`/private_networks`);
      }
    } else if (attributes?.module === 'virtual_machines') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        if (attributes?.one_id) {
          const encryptedParams = encryptQueryParams({
            order_id: attributes?.signature?.order_id,
            one_id: attributes?.signature?.one_id,
            region: attributes?.signature?.region,
          });
          navigate(`/virtual_machines/${attributes?.vmname}?${encryptedParams}`);
        } else {
          navigate(`/virtual_machines`);
        }
      }
    } else if (attributes?.module === 'virtual_gateway') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        const encryptedParams = encryptQueryParams({ key: attributes?.signature?.orderID });
        navigate(`/virtual_gateway/${attributes?.vroutername}?${encryptedParams}`);
      }
    } else if (attributes?.module === 'kubernetes') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        const encryptedParams = encryptQueryParams({ key: attributes?.signature?.orderID });
        navigate(`/kubernetes/${attributes?.servicename}?${encryptedParams}`);
      }
    }
    if (attributes?.module === 'permissions') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        if (attributes?.subModule === 'roles') navigate(`/permissions_roles/`);
        else if (attributes?.subModule === 'teams') navigate(`/permissions_teams/`);
      }
    } else if (attributes?.module === 'users') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        const encryptedParams = encryptQueryParams({
          customerID: attributes?.signature?.customerID,
          userID: attributes?.signature?.userID,
        });
        navigate(`/users/${attributes?.firstName + ' ' + attributes?.lastName}?${encryptedParams}`);
      }
    } else if (attributes?.module === 'audit_logs') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        navigate(`/audit_logs`);
      }
    } else if (attributes?.module === 'alert_contacts') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        navigate(`/alert_contacts`);
      }
    } else if (attributes?.module === 'customers') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        const encryptedParams = encryptQueryParams({
          customerID: attributes?.signature?.customerID,
          userID: attributes?.signature?.userID,
          subModule: attributes?.signature?.subModule,
        });
        navigate(`/company/${attributes?.customerName}?${encryptedParams}`);
      }
    } else if (attributes?.module === 'alerts_services') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        if (attributes?.subModule === 'alerts_services') navigate(`/alerts_services/enablealerts/`);
        else if (attributes?.subModule === 'tools_mapping') navigate(`/alerts_services/tools_mapping/`);
        else if (attributes?.subModule === 'project_mapping') navigate(`/alerts_services/tools_projects/`);
      }
    } else if (attributes?.module === 'tickets_dashboard') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        navigate(`/tickets_dashboard/`);
      }
    } else if (attributes?.module === 'alerts_tools') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        navigate(`/alerts_tools/`);
      }
    } else if (attributes?.module === 'alerts_policy') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        navigate(`/alerts_policy/`);
      }
    } else if (attributes?.module === 'alerts_company_service_list') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        navigate(`/alerts_company_service_list`);
      }
    } else if (attributes?.module === 'services') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        navigate(`/services`);
      }
    } else if (attributes?.module === 'time_reporting') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        const encryptedParams = encryptQueryParams({ key: attributes?.signature?.worklogKey });
        navigate(`/time_reporting/${attributes?.company}?${encryptedParams}`);
      }
    } else if (attributes?.module === 'control_group_templates') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        const encryptedParams = encryptQueryParams({ key: attributes?.signature?.controlGroupID });
        navigate(`/control_group_templates?${encryptedParams}`);
      }
    } else if (attributes?.module === 'regions') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        navigate(`/regions`);
      }
    } else if (attributes?.module === 'one_servers') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        navigate(`/one_servers`);
      }
    } else if (attributes?.module === 'vm_templates') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        navigate(`/vm_templates`);
      }
    } else if (attributes?.module === 'quickbooks') {
      if (attributes?.action === 'create' || attributes?.action === 'update') {
        navigate(`/logs`);
      }
    }
  };

  // const handleNotificationItemClick = (notificationID, attributes) => {
  //   const {module, action, subModule, signature, name, firstName, lastName, customerName, company} = attributes

  //   const indexOfClickedNotification = readNotificationIndices.indexOf(notificationID)
  //   setReadNotificationIndices(
  //     indexOfClickedNotification === -1
  //       ? [...readNotificationIndices, notificationID]
  //       : readNotificationIndices.filter(item => item !== notificationID),
  //   )

  //   setClickedNotificationId(notificationID)
  //   console.log('attributes==>', attributes)

  //   const navigateWithParams = (path, params) => navigate(`${path}?${encryptQueryParams(params)}`)

  //   const isActionCreateOrUpdate = action === 'create' || action === 'update'

  //   const moduleRoutes = {
  //     documents: () => {
  //       if (subModule === 'diagrams') {
  //         navigateWithParams(`/documents/diagrams/${name}`, {
  //           UniqueId: signature?.UniqueId,
  //           ServerRelativeURL: signature?.ServerRelativeURL,
  //         })
  //       } else if (subModule === 'files') navigate(`/documents/files/`)
  //     },
  //     projects: () => navigateWithParams(`/projects/${signature?.projectKeyID}`, {key: signature?.projectKeyID}),
  //     tickets: () => navigate(`/tickets/${signature?.key}`),
  //     reports: () => navigate(`/reports/`),
  //     vulnerability_scans: () =>
  //       navigateWithParams(`/vulnerability_scans/detail`, {assessmentID: signature?.assessmentID}),
  //     assessments: () => navigateWithParams(`/assessments/${name}`, {assessmentID: signature?.assessmentID}),
  //     virtual_machines_dashboard: () => navigate(`/virtual_machines_dashboard`),
  //     private_networks: () => navigate(`/private_networks`),
  //     virtual_machines: () => {
  //       if (signature?.one_id) {
  //         navigateWithParams(`/virtual_machines/${signature?.vmname}`, {
  //           order_id: signature?.order_id,
  //           one_id: signature?.one_id,
  //           region: signature?.region,
  //         })
  //       } else {
  //         navigate(`/virtual_machines`)
  //       }
  //     },
  //     virtual_gateway: () =>
  //       navigateWithParams(`/virtual_gateway/${signature?.vroutername}`, {orderID: signature?.orderID}),
  //     kubernetes: () => navigateWithParams(`/kubernetes/${signature?.servicename}`, {orderID: signature?.orderID}),
  //     permissions: () => {
  //       if (subModule === 'roles') navigate(`/permissions_roles/`)
  //       else if (subModule === 'teams') navigate(`/permissions_teams/`)
  //     },
  //     users: () =>
  //       navigateWithParams(`/users/${firstName + ' ' + lastName}`, {
  //         customerID: signature?.customerID,
  //         userID: signature?.userID,
  //       }),
  //     audit_logs: () => navigate(`/audit_logs`),
  //     alert_contacts: () => navigate(`/alert_contacts`),
  //     customers: () =>
  //       navigateWithParams(`/company/${customerName}`, {
  //         customerID: signature?.customerID,
  //         userID: signature?.userID,
  //         subModule: signature?.subModule,
  //       }),
  //     alerts_services: () => {
  //       if (subModule === 'alerts_services') navigate(`/alerts_services/enablealerts/`)
  //       else if (subModule === 'tools_mapping') navigate(`/alerts_services/tools_mapping/`)
  //       else if (subModule === 'project_mapping') navigate(`/alerts_services/tools_projects/`)
  //     },
  //     tickets_dashboard: () => navigate(`/tickets_dashboard/`),
  //     alerts_tools: () => navigate(`/alerts_tools/`),
  //     alerts_policy: () => navigate(`/alerts_policy/`),
  //     alerts_company_service_list: () => navigate(`/alerts_company_service_list`),
  //     services: () => navigate(`/services`),
  //     time_reporting: () => navigateWithParams(`/time_reporting/${company}`, {worklogKey: signature?.worklogKey}),
  //     control_group_templates: () =>
  //       navigateWithParams(`/control_group_templates`, {controlGroupID: signature?.controlGroupID}),
  //     regions: () => navigate(`/regions`),
  //     one_servers: () => navigate(`/one_servers`),
  //     vm_templates: () => navigate(`/vm_templates`),
  //     quickbooks: () => navigate(`/logs`),
  //   }

  //   if (isActionCreateOrUpdate && moduleRoutes[module]) {
  //     moduleRoutes[module]()
  //   }
  // }

  return (
    <NotificationBell data-testid='notification-bell'>
      <div onClick={handleNotificationClick}>
        <SidebarLinkIcon />
        {notificationCount > 0 && (
          <NotificationCount>{notificationLoading ? <EllipsisAnimation /> : notificationCount}</NotificationCount>
        )}
      </div>

      {isNotificationOpen && (
        <NotificationDropdown>
          <ScrollableContainer>
            <NotificationList>
              {notificationLoading ? (
                <LoadingWrap
                  height={20}
                  data-testid='loading-spinner'
                >
                  <SimpleLoader widthOrHeight={50} />
                </LoadingWrap>
              ) : notifications.length > 0 ? (
                notifications?.map((notification) => (
                  <NotificationItem
                    key={notification.notificationID}
                    id={notification.notificationID}
                    isOpen={notification.notificationID === clickedNotificationId}
                    isRead={isNotificationRead(notification.notificationID)}
                    message={notification.message}
                    time={notification.time}
                    handleNotificationItemClick={handleNotificationItemClick}
                    attributes={notification?.attributes}
                    role='listitem'
                  />
                ))
              ) : (
                <NoNotificationsText>No notifications found</NoNotificationsText>
              )}
            </NotificationList>
          </ScrollableContainer>
        </NotificationDropdown>
      )}
    </NotificationBell>
  );
};

export default NotificationIcon;

const NotificationItem = ({ id, isOpen, isRead, message, time, handleNotificationItemClick, attributes }) => {
  const handleClick = () => {
    handleNotificationItemClick(id, attributes);
  };

  const formatNotificationTime = (time) => {
    const currentTime = dayjs();
    const notificationTime = dayjs(time);

    if (notificationTime.isSame(currentTime, 'day')) {
      return notificationTime.format('HH:mm');
    } else if (notificationTime.isSame(currentTime.subtract(1, 'day'), 'day')) {
      return `Yesterday, ${notificationTime.format('HH:mm')}`;
    } else {
      return notificationTime.format('MM-DD-YYYY');
    }
  };

  return (
    <StyledNotificationItem
      onClick={handleClick}
      isRead={isRead}
      isOpen={isOpen}
    >
      <NotificationMessage>{ReactHtmlParser(message)}</NotificationMessage>
      <NotifcationDate>{formatNotificationTime(time)}</NotifcationDate>
    </StyledNotificationItem>
  );
};

const NotificationCount = styled.span`
  position: absolute;
  top: 4px;
  right: 5px;
  background-color: #ae1312;
  color: white;
  padding: 4px 8px;
  border-radius: 50%;
  font-size: 8px;
  font-weight: 900;
}
`;

const NotifcationDate = styled.div`
  text-align: center;
  width: 140px;
`;

const NotificationMessage = styled.div`
  width: 500px;
  // color: ${colorText};
`;

const SidebarLinkIcon = styled(NotificationsIcon)`
  z-index: 999;
  color: ${lighten(0.25, colorGray)};
  cursor: pointer;
`;

const NotificationDropdown = styled.div`
  position: absolute;
  color: ${lighten(0.25, colorGray)};
  top: 40px;
  right: 10px;
  background-color: ${colorBackground};
  border: 1px solid #ddd;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  border-radius: 4px;
`;

const NotificationList = styled.ul`
  list-style: none;
  color: ${lighten(0.25, colorGray)};
  padding: 0;
  // max-height: 300px;
  width: 400px;
  // overflow-y: auto;
`;
const ScrollableContainer = styled.div`
  max-height: 295px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${colorAccent};
    border-radius: 6px;
  }

  &::-webkit-scrollbar-track {
    background-color: #f4f4f4;
  }

  scrollbar-width: thin;
  scrollbar-color: ${colorAccent} #f4f4f4;
`;

const StyledNotificationItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid ${colorText};
  // transition: background-color 0.3s ease;
  font-size: 12px;

  background-color: ${({ isOpen, isRead }) => (isOpen || isRead ? colorBackground : colorNotificationBackgroundhover)};
  color: ${({ isOpen, isRead }) => (isOpen || isRead ? colorText : textcolorNotificationBackgroundhover)};
  font-weight: ${({ isOpen, isRead }) => (isOpen || isRead ? 'normal' : 'bold')};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    color: #fff;
    background-color: ${({ isOpen, isRead }) => (isOpen || isRead ? colorAccent : colorAccent)};
    cursor: pointer;
  }
`;
const NoNotificationsText = styled.p`
  text-align: center;
  margin-top: 20px;
  font-size: 16px;
  // color: ${colorGray};
`;

const NotificationBell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 60px;
`;
